import getI18nProps from '@/utils/i18/getI18nProps';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import point1 from './assets/point1.png';
import point2 from './assets/point2.png';
import point3 from './assets/point3.png';
import banner1 from './assets/banner1.webp';

import feature1 from './assets/feature-1.png';
import feature2 from './assets/feature-2.png';
import feature3 from './assets/feature3.png';
import feature4 from './assets/feature4.png';

import perk1 from './assets/perk-1.png';
import perk2 from './assets/perk-2.png';

import Image from 'next/image';
import WrappedButton from '@/components/ui/WrappedButton';
import { ArrowDropDown } from '@mui/icons-material';
import useTheme from '@/theme/useTheme';
import useLoginModal from '@/hooks/biz/useLoginModal';
import { useQuery } from '@tanstack/react-query';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import { useRouter } from 'next/router';
import useWalletModal from '@/hooks/biz/useWalletModal';

const SectionDisplay = (section) => {
  const { banner, title, description, buttons, index, anchor } = section;
  const { theme } = useTheme();

  return (
    <Stack
      id={anchor}
      sx={{
        flexDirection: ['column', 'column', index % 2 ? 'row-reverse' : 'row'],
        ['img']: {
          width: ['100%', '100%', '40%'],
          height: 'auto',
          objectFit: 'contain',
        },
      }}
    >
      <Image src={banner} alt="feature1" />
      <Stack gap={2} padding={2}>
        <Typography variant="h4" fontWeight={700}>
          {title}
        </Typography>
        <Typography>{description}</Typography>
        <Stack
          gap={2}
          direction={'row'}
          justifyContent={['center', 'center', 'flex-start']}
        >
          {buttons.map((button, index) => (
            <WrappedButton
              key={index}
              onClick={button.onClick}
              sx={{
                px: 0,

                flex: buttons.length > 1 ? '1' : undefined,
                width: buttons.length > 1 ? 'auto' : '50%',

                textTransform: 'uppercase',
              }}
              variant={index === 0 ? 'contained' : 'outlined'}
            >
              {button.label}
            </WrappedButton>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

const BenefitsOverview: React.FC = () => {
  const [t] = useTranslation(['benefits']);
  const { theme } = useTheme();

  const { push } = useRouter();

  const { data } = useQuery<any, any, any>(
    queryKeysFactory.rewards.dailyRewardsList(),
  );

  const hasNotDoneAnyDepositBonus = useMemo(() => {
    if (!data) return false;
    const status = data.data.rewards.find((r) => r.bonus === 'DEPOSITBONUS');
    return status.completedCount === 0 && status.currentInProgressIndex < 0;
  }, [data]);

  const [isLoggedIn, openLoginPopup] = useLoginModal();

  const openWallet = useWalletModal();

  const sectionConfigs = [
    {
      banner: feature1,
      title: t('benefits::Blast Off with a $8,000 Welcome Package!'),
      description: t(
        'benefits::At MoonClub, new players can grab a bonus on their first four deposits. Our generous welcome package offers a 650% bonus up to $8,000. This means you can get started with more bang for your buck and enjoy more play on the best casino games.',
      ),
      buttons: [
        {
          label: t('benefits::Claim Now'),
          onClick: () => {
            if (!isLoggedIn) {
              openLoginPopup();
            } else if (hasNotDoneAnyDepositBonus) {
              openWallet();
            } else {
              push('/casino');
            }
          },
        },
        {
          label: t('benefits::Find Out More'),
          onClick: () => {
            push('/benefits/deposit');
          },
        },
      ],
      anchor: '',
    },

    {
      banner: feature2,
      title: t('benefits::EARN OVER 15% LIFETIME REFERRAL COMMISSION'),
      description: (
        <Trans
          i18nKey={
            'Refer your friends to MoonClub and enjoy over 15% lifetime commission through our multi-tier referral program.<br/><br/>Invite your friends and enjoy over 15% lifetime commission through our multi-tier referral program.<br/><br/>Get 15% commission for direct referrals, 6% commission for second-tier referrals (your friend’s referrals), and 4% for third-tier referrals (your friend’s friend’s referrals).'
          }
          ns={'benefits'}
        ></Trans>
      ),
      buttons: [
        {
          label: isLoggedIn
            ? t('benefits::Invite Friends')
            : t('benefits::Claim Now'),
          onClick: () => {
            if (!isLoggedIn) {
              openLoginPopup();
            } else {
              push('/profile/referral');
            }
          },
        },
      ],
      anchor: 'earn-lifetime-commission',
    },
    {
      banner: feature3,
      title: t('benefits::MAXIMIZE THE POTENTIAL OF RAKEBACK'),
      description: t(
        'benefits::You can enjoy money back on every bet through our Rakeback program. The money is credited to your account daily.',
      ),
      buttons: [
        {
          label: t('benefits::Play Now'),
          onClick: () => {
            if (!isLoggedIn) {
              openLoginPopup();
            } else {
              push('/casino');
            }
          },
        },
        {
          label: t('benefits::Find Out More'),
          onClick: () => {
            push('/benefits/rakeback');
          },
        },
      ],
    },
  ];

  return (
    <Stack>
      <Stack
        gap={1}
        sx={{
          backgroundImage: `url(${banner1.src})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right',
          [theme.breakpoints.down('md')]: {
            background: 'none',
          },
        }}
      >
        <Typography
          variant="h1"
          fontSize={[22, 26, 32, 45]}
          fontWeight={700}
          letterSpacing={'0.92px'}
          textAlign={['center', 'center', 'start']}
        >
          {t('benefits::UNLOCK ENDLESS BENEFITS')}
        </Typography>
        <Typography
          color={'text.secondary'}
          fontSize={16}
          textAlign={['center', 'center', 'start']}
        >
          {t('benefits::Join now and embark on a rewarding gaming journey!')}
        </Typography>
        <Stack gap={2} mt={4}>
          <Stack direction={'row'} gap={2}>
            <Image src={point1} alt="point1" width={34} height={42} />
            <Box>
              <Typography>{t('benefits::6% RAKEBACK')}</Typography>
              <Typography color={'text.secondary'}>
                {t('benefits::Get money back on every bet through Rakeback')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <Image src={point2} alt="point2" width={34} height={34} />
            <Box>
              <Typography>{t('benefits::REGULAR BONUSES')}</Typography>
              <Typography color={'text.secondary'}>
                {t('benefits::Enjoy our balance-boosting bonuses')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <Image src={point3} alt="point3" width={34} height={42} />
            <Box>
              <Typography>
                {t('benefits::WEEKLY & MONTHLY CASHBACK')}
              </Typography>
              <Typography color={'text.secondary'}>
                {t('benefits::Play with the protection of cashback rewards')}
              </Typography>
            </Box>
          </Stack>
        </Stack>
        <Box
          display={['block', 'block', 'none']}
          sx={{
            ['img']: {
              width: '100%',
              height: 'auto',
            },
          }}
        >
          <Image src={banner1} alt="point1" />
        </Box>
        <WrappedButton
          sx={{
            alignSelf: ['center', 'center', 'flex-start'],
            px: 8,
            mt: 2,
          }}
          onClick={() => {
            if (!isLoggedIn) {
              openLoginPopup();
            }
          }}
        >
          {t('benefits::Join Now')}
        </WrappedButton>
      </Stack>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        my={4}
        display={['none', 'none', 'flex']}
      >
        {[
          t('benefits::Register and Deposit'),
          t('benefits::Play Real Money Games'),
          t('benefits::Earn Bonus Rewards'),
        ].map((item, index) => (
          <Stack key={index} direction={'row'} gap={1} alignItems={'center'}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                color: 'black',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >{`0${index + 1}`}</Box>
            <Typography fontSize={18} fontWeight={600}>
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Stack gap={4}>
        {sectionConfigs.map((section, index) => (
          <SectionDisplay key={index} {...section} index={index} />
        ))}
      </Stack>
      {/*<Stack mt={2} gap={2}>*/}
      {/*  <Typography*/}
      {/*    variant="h3"*/}
      {/*    fontWeight={700}*/}
      {/*    textTransform={'uppercase'}*/}
      {/*    textAlign={'center'}*/}
      {/*  >*/}
      {/*    {t('benefits::More Perks')}*/}
      {/*  </Typography>*/}
      {/*  <Stack*/}
      {/*    direction={'row'}*/}
      {/*    gap={[1, 1, 4]}*/}
      {/*    justifyContent={'center'}*/}
      {/*    sx={{*/}
      {/*      ['img']: {*/}
      {/*        maxWidth: 335,*/}
      {/*        height: 'auto',*/}
      {/*        [theme.breakpoints.down('md')]: {*/}
      {/*          width: '50%',*/}
      {/*        },*/}
      {/*      },*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Image src={perk1} alt="perk1" />*/}
      {/*    <Image src={perk2} alt="perk2" />*/}
      {/*  </Stack>*/}
      {/*</Stack>*/}
      {/*<Typography*/}
      {/*  mt={4}*/}
      {/*  mb={2}*/}
      {/*  variant="h4"*/}
      {/*  fontWeight={700}*/}
      {/*  textTransform={'uppercase'}*/}
      {/*  textAlign={'center'}*/}
      {/*>*/}
      {/*  {t('benefits::Weekly, and Monthly Rewards!')}*/}
      {/*</Typography>*/}
      {/*<Typography color={'text.secondary'}>*/}
      {/*  {t(*/}
      {/*    'benefits::The weekly and monthly rewards are generated based on your wagering activity. All rewards are credited to your account as real money.',*/}
      {/*  )}*/}
      {/*</Typography>*/}
      {/*<br />*/}
      {/*<Typography color={'text.secondary'}>*/}
      {/*  {t(*/}
      {/*    'A percentage of the earned rewards will be split across several days for you to claim. The rewards will be summed up if more than one split amount occurs on the same day. For example, 20% of you reward will be available for claim right away, while the other 80% is split over the next 7 days to be claimed daily.',*/}
      {/*  )}*/}
      {/*</Typography>*/}
      {/*<Accordion*/}
      {/*  sx={{*/}
      {/*    border: (theme) => theme.palette.border.normal,*/}
      {/*    borderRadius: 1,*/}
      {/*    mt: 2,*/}
      {/*  }}*/}
      {/*>*/}
      {/*  <AccordionSummary*/}
      {/*    expandIcon={<ArrowDropDown />}*/}
      {/*    aria-controls="panel1-content"*/}
      {/*    id="panel1-header"*/}
      {/*    sx={{*/}
      {/*      padding: 2,*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Typography>{t('benefits::Find Out More')}</Typography>*/}
      {/*  </AccordionSummary>*/}
      {/*  <AccordionDetails>*/}
      {/*    <Divider />*/}
      {/*    <Typography fontWeight={700} mt={2}>*/}
      {/*      {t('benefits::WEEKLY REWARDS')}*/}
      {/*    </Typography>*/}
      {/*    <br />*/}
      {/*    <Typography variant="caption" color={'text.secondary'}>*/}
      {/*      <Trans*/}
      {/*        i18nKey={*/}
      {/*          'The bonus amount is based on your total wagers in the last 7 days.<br/><br/>For example, if you wagered a total of $1,000 in the last 7 days, the weekly reward amount would be $0.05.<br/><br/>Formula: 1,000 ($ wagered) x 0.01(%) x 0.05 (%) = 0.50 ($)<br/><br/>The weekly reward will be credited every Friday at 00:00 UTC. A minimum total wager of $1,000 in the last 7 days is required. It will expires if you do not claim it before the next one is computed.'*/}
      {/*        }*/}
      {/*        ns="benefits"*/}
      {/*      ></Trans>*/}
      {/*    </Typography>*/}
      {/*    <br />*/}
      {/*    <br />*/}
      {/*    <Typography fontWeight={700}>*/}
      {/*      {t('benefits::MONTHLY REWARDS')}*/}
      {/*    </Typography>*/}
      {/*    <br />*/}
      {/*    <Typography variant="caption" color={'text.secondary'}>*/}
      {/*      <Trans*/}
      {/*        i18nKey={*/}
      {/*          'The bonus amount is based on your total wagers in the last month.<br/><br/>For example, if you wagered a total of $10,000 in the last month, the monthly reward amount would be $5.00.<br/><br/>Formula: 10,000 ($ wagered) x 0.01(%) x 0.05 (%) = 5 ($)<br/><br/>The monthly reward will be credited on the 30th of each month at 00:00 UTC. A minimum total wager of $10,000 in the last month is required. It will expires if you do not claim it before the next one is computed.'*/}
      {/*        }*/}
      {/*        ns="benefits"*/}
      {/*      ></Trans>*/}
      {/*    </Typography>*/}
      {/*  </AccordionDetails>*/}
      {/*</Accordion>*/}
      {/*<WrappedButton*/}
      {/*  sx={{*/}
      {/*    mt: 4,*/}
      {/*    mb: 2,*/}
      {/*    px: 8,*/}
      {/*    textTransform: 'uppercase',*/}
      {/*    alignSelf: 'center',*/}
      {/*  }}*/}
      {/*  onClick={() => {*/}
      {/*    if (!isLoggedIn) {*/}
      {/*      openLoginPopup();*/}
      {/*    }*/}
      {/*  }}*/}
      {/*>*/}
      {/*  {t('Join Now')}*/}
      {/*</WrappedButton>*/}
      <SectionDisplay
        index={1}
        banner={feature4}
        title={t('benefits::PLAY DIRECTLY ON TELEGRAM')}
        description={t(
          'benefits::You can access MoonClub directly on Telegram, giving a fully anonymous casino experience.',
        )}
        buttons={[
          {
            label: t('benefits::Play on Telegram'),
            onClick: () => {},
          },
        ]}
      />
    </Stack>
  );
};

// export const runtime = 'experimental-edge';

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await getI18nProps({ locale, additionalNamespaces: ['benefits'] })),
    },
  };
}

export default BenefitsOverview;
